import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/modal/Modal";
import { useCallback, useEffect, useState, type FC } from "react";

import { useToast } from "@/components/podkit/toasts/use-toast";
import { useCreateRemoteRunner } from "@/queries/runner-queries";
import { NewAWSRunnerForm } from "@/components/runners/NewAWSRunnerForm";
import { formatError } from "@/utils/errors";
import { useNavigate } from "react-router-dom";
import { TrackLocations } from "@/hooks/use-segment";
import { AWSLogo } from "@/assets/logos/AWSLogo";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { NewLinuxRunnerForm } from "@/components/runners/NewLinuxRunnerForm";
import { ProviderOption } from "@/components/runners/ProviderOption";
import { LinuxLogo } from "@/assets/logos/LinuxLogo";
import { AzureLogo } from "@/assets/logos/AzureLogo";
import { GCPLogo } from "@/assets/logos/GPCLogo";

type Props = {
    open: boolean;
    onClose: () => void;
};

type ModalState = {
    screen: "type-selection" | "configuration";
    selectedProvider?: RunnerProvider;
};

export const NewRunnerModal: FC<Props> = ({ open, onClose }) => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const createRunner = useCreateRemoteRunner();
    const [disabled, setDisabled] = useState(false);
    const [modalState, setModalState] = useState<ModalState>({ screen: "type-selection" });

    // Reset the modal state when the modal is opened
    useEffect(() => {
        if (open) {
            setModalState({
                screen: "type-selection",
            });
        }
    }, [open]);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const handleProviderSelect = useCallback((provider: RunnerProvider) => {
        setModalState({ screen: "configuration", selectedProvider: provider });
    }, []);

    const handleBack = useCallback(() => {
        setModalState({ screen: "type-selection" });
    }, []);

    const handleSubmit = useCallback<(values: {
        name: string;
        region?: string;
        provider: RunnerProvider
    }) => Promise<void>>(
        async ({ name, region, provider }) => {
            try {
                const { runner } = await createRunner.mutateAsync({ name, region, provider });
                navigate(`/settings/runners/${runner.runnerId}`);
            } catch (error) {
                toast({ title: "Failed to create a runner", description: formatError(error) });
            }
        },
        [createRunner, navigate, toast],
    );

    const modalTitle = (modalState: ModalState) => {
        if (modalState.screen === "type-selection") {
            return "Add a runner";
        }

        switch (modalState.selectedProvider) {
            case RunnerProvider.AWS_EC2:
                return "Create AWS runner";
            case RunnerProvider.LINUX_HOST:
                return "Create Linux runner";
            default:
                return "";
        }
    };

    const modalDescription = (modalState: ModalState) => {
        if (modalState.screen === "type-selection") {
            return "Choose which provider to use for running your environments";
        }

        return "";
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-[600px]" data-track-location={TrackLocations.NewRunnerModal}>
                <DialogHeader>
                    <DialogTitle>
                        {modalTitle(modalState)}
                    </DialogTitle>
                    <DialogDescription>
                        {modalDescription(modalState)}
                    </DialogDescription>
                </DialogHeader>

                <DialogBody>
                    {modalState.screen === "type-selection" && (
                        <div className="flex flex-col gap-2">
                            <ProviderOption name="AWS" Logo={AWSLogo}
                                            onClick={() => handleProviderSelect(RunnerProvider.AWS_EC2)} />
                            <ProviderOption name="Linux" Logo={LinuxLogo}
                                            onClick={() => handleProviderSelect(RunnerProvider.LINUX_HOST)} />
                            <ProviderOption name="Azure" Logo={AzureLogo} disabled />
                            <ProviderOption name="GCP" Logo={GCPLogo} disabled />
                        </div>
                    )}
                    {(modalState.screen === "configuration" && modalState.selectedProvider === RunnerProvider.AWS_EC2) && (
                        <NewAWSRunnerForm
                            formId="new-runner-form"
                            onSubmit={(values) => handleSubmit({ ...values, provider: RunnerProvider.AWS_EC2 })}
                            onDisabledChange={setDisabled}
                        />
                    )}
                    {(modalState.screen === "configuration" && modalState.selectedProvider === RunnerProvider.LINUX_HOST) && (
                        <NewLinuxRunnerForm
                            formId="new-runner-form"
                            onSubmit={(values) => handleSubmit({ ...values, provider: RunnerProvider.LINUX_HOST })}
                            onDisabledChange={setDisabled}
                        />
                    )}
                </DialogBody>

                {modalState.screen === "configuration" && (
                    <DialogFooter>
                        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-between">
                            <div>
                                {modalState.screen === "configuration" && (
                                    <Button
                                        variant="secondary"
                                        type="button"
                                        onClick={handleBack}
                                    >
                                        Back
                                    </Button>
                                )}
                            </div>
                            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                                <DialogClose asChild>
                                    <Button type="button" variant="secondary">
                                        Cancel
                                    </Button>
                                </DialogClose>
                                <Button
                                    loading={createRunner.isPending}
                                    autoFocus={true}
                                    aria-label="create"
                                    type="submit"
                                    data-testid="create"
                                    variant="brand"
                                    form="new-runner-form"
                                    disabled={disabled}
                                >
                                    Create
                                </Button>
                            </div>
                        </div>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
};
